import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, delay, mergeMap, switchMap } from 'rxjs/operators';
import { EnumHomesActions, GetHomeConfigSuccess, GetHomesSuccess, GetHomeStatusSuccess, GetReadonlyDeviceStateSuccess } from '../homes/homes.action';
import { RoomsActions } from '../rooms/rooms.action';
import { ModulesActions } from './modules.action';
import { ModulesService } from './modules.service';

@Injectable()
export class ModulesEffects {
  private readonly actions$ = inject(Actions);
  private readonly moduleService = inject(ModulesService);

  getModules$ = createEffect(() => this.actions$.pipe(
    ofType(EnumHomesActions.GetHomesSuccess),
    switchMap((action) => [ModulesActions.getModulesSuccess({ payload: action.payload, modules: action.modules })]),
  ));

  updateConfig$ = createEffect(() => this.actions$.pipe(
    ofType(EnumHomesActions.GetHomeConfigSuccess),
    switchMap((action) => [ModulesActions.updateModulesGetConfig({ payload: action.payload })]),
  ));

  updateStatus$ = createEffect(() => this.actions$.pipe(
    ofType(EnumHomesActions.GetHomeStatusSuccess),
    switchMap((action) => [ModulesActions.updateModulesStatus({ payload: action.payload })]),
  ));

  updateReadonlyDeviceState$ = createEffect(() => this.actions$.pipe(
    ofType(EnumHomesActions.GetReadonlyDeviceStateSuccess),
    switchMap(({ modules, errors }) => {
      return [
        ModulesActions.updateReadOnlyDevicesStatus({ payload: modules }),
        ModulesActions.setModulesStatusErrors({ payload: { id: '', errors: errors ?? [] } }),
      ];
    }),
  ));

  updateModuleName$ = createEffect(() => this.actions$.pipe(
    ofType(ModulesActions.updateModuleName),
    mergeMap((action) => {
      let apiObservable: Observable<any>;
      if (action.payload.module_id !== action.payload.device_id) {
        apiObservable = this.moduleService.updateModule(action.payload, action.header);
      }
      else {
        apiObservable = this.moduleService.updateDevice(action.payload);
      }
      return apiObservable.pipe(
        switchMap(() => [
          ModulesActions.updateModuleNameSuccess({ payload: action.payload }),
        ]),
        catchError(({ error }) => of(ModulesActions.updateModuleNameFailure({ payload: error }))),
      );
    }),
  ));

  updateModuleRoom$ = createEffect(() => this.actions$.pipe(
    ofType(ModulesActions.updateModuleRoom),
    switchMap((action) => {
      return this.moduleService.updateModuleRoom(action.payload, action.header).pipe(
        delay(1000),
        switchMap(() => [
          ModulesActions.updateModuleRoomSuccess({ payload: action.payload }),
        ]),
        catchError(({ error }) => of(ModulesActions.updateModuleRoomFailure({ payload: error }))),
      );
    }),
  ));

  removeModuleFromHome$ = createEffect(() => this.actions$.pipe(
    ofType(ModulesActions.removeModuleFromHome),
    switchMap((action) => {
      return this.moduleService.removeDeviceFromHome(action.payload).pipe(
        switchMap(() => [
          ModulesActions.removeModuleFromHomeSuccess({ payload: action.payload }),
          RoomsActions.removeModuleFromRoom({ payload: { module_id: action.payload.device_id, room_id: action.payload.room_id } }),
        ]),
        catchError(({ error }) => of(ModulesActions.removeModuleFromHomeFailure({ payload: error }))),
      );
    }),
  ));

  changePolarity$ = createEffect(() => this.actions$.pipe(
    ofType(ModulesActions.changePolarity),
    switchMap((action) => {
      return this.moduleService.changePolarity(action.payload).pipe(
        switchMap(() => [
          ModulesActions.changePolaritySuccess({ payload: action.payload }),
        ]),
        catchError(({ error }) => of(ModulesActions.changePolarityFailure({ payload: error }))),
      );
    }),
  ));

  RemoveDataGateway$ = createEffect(() => this.actions$.pipe(
    ofType(ModulesActions.removeDataGateway),
    switchMap((action) => {
      return this.moduleService.removeDataGateway(action.payload).pipe(
        switchMap(() => [
          ModulesActions.removeDataGatewaySuccess({ payload: action.payload }),
        ]),
        catchError(({ error }) => of(ModulesActions.removeDataGatewayFailure({ payload: error }))),
      );
    }),
  ));

  TransferModuleMeasurements$ = createEffect(() => this.actions$.pipe(
    ofType(ModulesActions.transferModuleMeasurements),
    switchMap((action) => {
      return this.moduleService.transferModuleMeasurements(action.payload).pipe(
        switchMap(() => [
          ModulesActions.transferModuleMeasurementsSuccess({ payload: action.payload }),
        ]),
        catchError(({ error }) => of(ModulesActions.transferModuleMeasurementsFailure({ payload: error }))),
      );
    }),
  ));
}
